// The backend client is heavy (auth + realtime + storage) and is only needed
// for fire-and-forget counters, so load it lazily to keep it out of the
// first-load bundle.
const getSupabase = async () => (await import("@/integrations/supabase/client")).supabase;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Send a Google Analytics 4 event. Safe no-op if gtag is not loaded
 * (e.g. ad blockers, dev environments).
 */
export const trackGAEvent = (
  eventName: string,
  params: Record<string, unknown> = {},
): void => {
  if (typeof window === "undefined") return;
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }
  } catch (err) {
    // Never let analytics break the UI
    console.warn("[analytics] gtag failed", err);
  }
};

/**
 * Track a blog post page view: GA4 event + DB counter.
 * Fire-and-forget; errors are logged but never thrown.
 */
export const trackBlogView = async (
  postId: string,
  meta: { slug: string; title: string; language: string },
): Promise<void> => {
  trackGAEvent("blog_view", {
    post_id: postId,
    post_slug: meta.slug,
    post_title: meta.title,
    language: meta.language,
  });

  try {
    const sb = await getSupabase();
    const { error } = await sb.rpc("increment_blog_post_view", {
      _post_id: postId,
    });
    if (error) console.warn("[analytics] increment_blog_post_view failed", error);
  } catch (err) {
    console.warn("[analytics] increment_blog_post_view threw", err);
  }
};

/**
 * Track a click on an external (e.g. LinkedIn) link from a blog card.
 * Non-blocking - link navigates immediately.
 */
export const trackBlogExternalClick = (
  postId: string,
  meta: { slug: string; title: string; url: string; language: string },
): void => {
  trackGAEvent("blog_external_click", {
    post_id: postId,
    post_slug: meta.slug,
    post_title: meta.title,
    outbound_url: meta.url,
    language: meta.language,
  });

  // Best-effort DB increment - don't await so the click feels instant.
  void getSupabase().then((sb) =>
    sb.rpc("increment_blog_post_external_click", { _post_id: postId }).then(({ error }) => {
      if (error)
        console.warn("[analytics] increment_blog_post_external_click failed", error);
    }),
  );
};

// ---------------------------------------------------------------------------
// Conversion events: Book a call CTA + Newsletter form submissions
// ---------------------------------------------------------------------------

/**
 * Track a click on the "Book a Free 15-Minute Call" (Calendly) CTA.
 * Fires both a custom `book_call_click` event and the GA4 recommended
 * `generate_lead` conversion event so it can be marked as a conversion
 * in Google Analytics.
 */
export const trackBookCallClick = (
  location: string,
  meta: { language?: string; calendlyUrl?: string; extra?: Record<string, unknown> } = {},
): void => {
  const params = {
    cta_location: location,
    method: "calendly",
    outbound_url: meta.calendlyUrl,
    language: meta.language,
    ...(meta.extra ?? {}),
  };
  trackGAEvent("book_call_click", params);
  // GA4 recommended conversion event
  trackGAEvent("generate_lead", {
    ...params,
    currency: "EUR",
    value: 0,
    lead_source: "book_call",
  });
};

/**
 * Track a successful newsletter form submission. Fires `newsletter_signup`
 * (custom) and `generate_lead` (GA4 recommended conversion event).
 * Only call AFTER the submission succeeded.
 */
export const trackNewsletterSignup = (
  location: string,
  meta: { language?: string | undefined; extra?: Record<string, unknown> | undefined } = {},
): void => {
  const params = {
    form_location: location,
    method: "email",
    language: meta.language,
    ...(meta.extra ?? {}),
  };
  trackGAEvent("newsletter_signup", params);
  trackGAEvent("sign_up", { ...params, method: "newsletter" });
  trackGAEvent("generate_lead", {
    ...params,
    currency: "EUR",
    value: 0,
    lead_source: "newsletter",
  });
};

export type BlogEngagementKind =
  | "scroll_25"
  | "scroll_50"
  | "scroll_75"
  | "scroll_100"
  | "cta_newsletter_submit"
  | "cta_book_call_click"
  | "cta_dismiss";

/**
 * Track a generic blog engagement signal: GA4 event + DB counter.
 * Fire-and-forget. Errors are swallowed so analytics never breaks UX.
 */
export const trackBlogEngagement = (
  kind: BlogEngagementKind,
  meta: { postId: string; slug?: string | undefined; title?: string | undefined; language?: string | undefined; extra?: Record<string, unknown> | undefined },
): void => {
  trackGAEvent(`blog_${kind}`, {
    post_id: meta.postId,
    post_slug: meta.slug,
    post_title: meta.title,
    language: meta.language,
    ...(meta.extra ?? {}),
  });

  void getSupabase().then((sb) =>
    sb
      .rpc("increment_blog_post_engagement", { _post_id: meta.postId, _kind: kind })
      .then(({ error }) => {
        if (error)
          console.warn(`[analytics] increment_blog_post_engagement(${kind}) failed`, error);
      }),
  );
};

// ---------------------------------------------------------------------------
// Generic page engagement tracking (CTAs, scroll depth on landing pages)
// ---------------------------------------------------------------------------

export type InterimEngagementKind =
  | "scroll_25"
  | "scroll_50"
  | "scroll_75"
  | "scroll_100"
  | "cta_hero_primary"
  | "cta_hero_secondary"
  | "cta_pricing_basic"
  | "cta_pricing_standard"
  | "cta_pricing_deep_dive"
  | "cta_final"
  | "cta_brief_download_main"
  | "cta_brief_download_en"
  | "cta_brief_download_pl"
  | "cta_brief_download_by"
  | "faq_toggle";

export type PageKey = "interim_management";

/**
 * Track an engagement signal on a landing page (e.g. CTA click, scroll
 * milestone). Sends a GA4 event AND increments a server-side counter via
 * a SECURITY DEFINER RPC with an allowlist of pages and kinds.
 * Fire-and-forget; analytics never block the UI.
 */
export const trackPageEngagement = (
  page: PageKey,
  kind: InterimEngagementKind,
  meta: { language?: string | undefined; extra?: Record<string, unknown> | undefined } = {},
): void => {
  trackGAEvent(`${page}_${kind}`, {
    page_key: page,
    language: meta.language,
    ...(meta.extra ?? {}),
  });

  void getSupabase().then((sb) =>
    sb
      .rpc("increment_page_engagement", {
        _page_key: page,
        _kind: kind,
        ...(meta.language !== undefined ? { _language: meta.language } : {}),
      })
      .then(({ error }) => {
        if (error)
          console.warn(`[analytics] increment_page_engagement(${page}/${kind}) failed`, error);
      }),
  );
};

